import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Loader } from 'lucide-react';

export default function AuthPage({ initialMode = 'login' }) {
  const [mode, setMode]             = useState(initialMode);
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError]           = useState('');
  const [info, setInfo]             = useState('');
  const [loading, setLoading]       = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendInfo, setResendInfo] = useState('');

  const reset = () => { setError(''); setInfo(''); setShowResend(false); setResendInfo(''); };
  const switchMode = (m) => { reset(); setMode(m); };

  // ── Resend confirmation email ──────────────────────────────────────────────
  const handleResend = async () => {
    if (!email) { setResendInfo('Fyll i din e-postadress ovan först.'); return; }
    setResendLoading(true);
    setResendInfo('');
    try {
      const { error: err } = await supabase.auth.resend({ type: 'signup', email });
      if (err) setResendInfo(`Kunde inte skicka: ${err.message}`);
      else setResendInfo('Bekräftelsemail skickat! Kontrollera din inkorg (och skräppost).');
    } catch (_) {
      setResendInfo('Något gick fel. Försök igen.');
    } finally {
      setResendLoading(false);
    }
  };

  // ── Main form submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    reset();

    // Recovery — set new password
    if (mode === 'recovery') {
      if (newPassword.length < 8) { setError('Lösenordet måste vara minst 8 tecken.'); return; }
      setLoading(true);
      try {
        const { error: err } = await supabase.auth.updateUser({ password: newPassword });
        if (err) throw err;
        setInfo('Lösenordet uppdaterat! Du är nu inloggad.');
      } catch (err) {
        setError(err.message || 'Kunde inte uppdatera lösenordet.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Forgot password — send reset email
    if (mode === 'forgot') {
      if (!email) { setError('Fyll i din e-postadress.'); return; }
      setLoading(true);
      try {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (err) throw err;
        setInfo('Om e-postadressen finns i systemet har ett återställningsmail skickats. Kontrollera din inkorg (och skräppost).');
      } catch (err) {
        // Guard against the Supabase SDK "body stream already read" bug
        // — the request succeeded even though the SDK threw
        if (err.message?.includes('body stream already read')) {
          setInfo('Om e-postadressen finns i systemet har ett återställningsmail skickats. Kontrollera din inkorg (och skräppost).');
        } else {
          setError(err.message || 'Kunde inte skicka återställningsmail.');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Login / Register
    if (!email || !password) { setError('Fyll i e-post och lösenord.'); return; }
    if (password.length < 8)  { setError('Lösenordet måste vara minst 8 tecken.'); return; }

    setLoading(true);
    try {
      if (mode === 'login') {
        let realErrorCode = null;
        let realErrorMsg  = null;
        let sdkSucceeded  = false;

        try {
          const { error: err } = await supabase.auth.signInWithPassword({ email, password });
          if (!err) {
            sdkSucceeded = true;
          } else {
            const isStreamBug = err.message?.includes('body stream already read');
            if (!isStreamBug) {
              realErrorCode = err.code || err.error_code;
              realErrorMsg  = err.message;
            }
          }
        } catch (_) {}

        if (!sdkSucceeded && !realErrorMsg) {
          try {
            const res = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/token?grant_type=password`,
              {
                method: 'POST',
                headers: {
                  'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
              }
            );
            if (res.status === 400)      realErrorCode = 'email_not_confirmed';
            else if (res.status === 422) realErrorCode = 'invalid_credentials';
            else if (!res.ok)            realErrorMsg = `Inloggning misslyckades (${res.status}).`;
            else                         sdkSucceeded = true;
          } catch (_) {
            realErrorMsg = 'Inloggning misslyckades. Kontrollera din internetanslutning.';
          }
        }

        if (!sdkSucceeded) {
          if (realErrorCode === 'email_not_confirmed') {
            setError('E-postadressen är inte bekräftad. Klicka på länken i bekräftelsemailet.');
            setShowResend(true);
          } else if (realErrorCode === 'invalid_credentials') {
            setError('Felaktig e-postadress eller lösenord.');
          } else {
            setError(realErrorMsg || 'Inloggning misslyckades.');
          }
        }
      } else {
        const { error: err } = await supabase.auth.signUp({ email, password });
        if (err) throw err;
        setInfo('Kontot skapat! Kontrollera din e-post för att bekräfta adressen, logga sedan in.');
        switchMode('login');
      }
    } catch (err) {
      setError(err.message || 'Något gick fel.');
    } finally {
      setLoading(false);
    }
  };

  // ── Logo block (shared) ────────────────────────────────────────────────────
  const Logo = () => (
    <div className="auth-logo" style={{
      display: 'flex', flexDirection: 'row', alignItems: 'center',
      justifyContent: 'center', gap: 14, marginBottom: 32,
    }}>
      <img
        src="/vv-logo.svg"
        alt="Vårdväskan"
        width={48}
        height={48}
        style={{ display: 'block', borderRadius: 11, flexShrink: 0 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, alignItems: 'flex-start' }}>
        <span className="auth-logo-text" style={{ fontSize: 19, letterSpacing: '-0.01em' }}>Vårdväskan</span>
        <span className="auth-logo-sub" style={{ marginTop: 3, letterSpacing: '0.14em' }}>AI Studio</span>
      </div>
    </div>
  );

  // ── Recovery mode ──────────────────────────────────────────────────────────
  if (mode === 'recovery') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <Logo />
          <h2 className="auth-section-title">Sätt nytt lösenord</h2>
          <p className="auth-section-subtitle">Välj ett nytt lösenord (minst 8 tecken).</p>
          <form className="auth-form" onSubmit={handleSubmit} data-testid="auth-form">
            {error && <div className="auth-msg auth-msg--error" data-testid="auth-error">{error}</div>}
            {info  && <div className="auth-msg auth-msg--info"  data-testid="auth-info">{info}</div>}
            <div className="auth-field">
              <label className="auth-label">Nytt lösenord</label>
              <input
                className="auth-input"
                type="password"
                placeholder="Minst 8 tecken"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="new-password-input"
                autoFocus
              />
            </div>
            <button className="auth-submit-btn" type="submit" disabled={loading} data-testid="auth-submit-btn">
              {loading ? <><Loader size={15} className="auth-spinner" /> Vänta…</> : 'Spara nytt lösenord'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Forgot password mode ───────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <Logo />
          <h2 className="auth-section-title">Återställ lösenord</h2>
          <p className="auth-section-subtitle">Fyll i din e-post — vi skickar en länk för att välja ett nytt lösenord.</p>
          <form className="auth-form" onSubmit={handleSubmit} data-testid="auth-form">
            {error && <div className="auth-msg auth-msg--error" data-testid="auth-error">{error}</div>}
            {info  && <div className="auth-msg auth-msg--info"  data-testid="auth-info">{info}</div>}
            <div className="auth-field">
              <label className="auth-label">E-postadress</label>
              <input
                className="auth-input"
                type="email"
                placeholder="namn@vardvaskan.se"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="email-input"
                autoFocus
              />
            </div>
            <button className="auth-submit-btn" type="submit" disabled={loading} data-testid="auth-submit-btn">
              {loading ? <><Loader size={15} className="auth-spinner" /> Väntar…</> : 'Skicka återställningsmail'}
            </button>
          </form>
          <button className="auth-back-link" onClick={() => switchMode('login')} data-testid="back-to-login-btn">
            ← Tillbaka till inloggning
          </button>
        </div>
      </div>
    );
  }

  // ── Login / Register mode ──────────────────────────────────────────────────
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Logo />

        <div className="auth-tabs">
          <button
            className={`auth-tab${mode === 'login' ? ' auth-tab--active' : ''}`}
            onClick={() => switchMode('login')}
            data-testid="tab-login"
          >
            Logga in
          </button>
          <button
            className={`auth-tab${mode === 'register' ? ' auth-tab--active' : ''}`}
            onClick={() => switchMode('register')}
            data-testid="tab-register"
          >
            Skapa konto
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} data-testid="auth-form">
          {error && <div className="auth-msg auth-msg--error" data-testid="auth-error">{error}</div>}

          {showResend && (
            <div className="auth-resend-wrap" data-testid="resend-wrap">
              <button
                type="button"
                className="auth-resend-btn"
                onClick={handleResend}
                disabled={resendLoading}
                data-testid="resend-btn"
              >
                {resendLoading
                  ? <><Loader size={13} className="auth-spinner" /> Skickar…</>
                  : '↻  Skicka bekräftelsemail igen'}
              </button>
              {resendInfo && <span className="auth-resend-info" data-testid="resend-info">{resendInfo}</span>}
            </div>
          )}

          {info && <div className="auth-msg auth-msg--info" data-testid="auth-info">{info}</div>}

          <div className="auth-field">
            <label className="auth-label">E-postadress</label>
            <input
              className="auth-input"
              type="email"
              placeholder="namn@vardvaskan.se"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="email-input"
            />
          </div>

          <div className="auth-field">
            <label className="auth-label">Lösenord</label>
            <input
              className="auth-input"
              type="password"
              placeholder="Minst 8 tecken"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              data-testid="password-input"
            />
          </div>

          {mode === 'login' && (
            <div className="auth-forgot-row">
              <button
                type="button"
                className="auth-forgot-link"
                onClick={() => switchMode('forgot')}
                data-testid="forgot-password-btn"
              >
                Glömt lösenordet?
              </button>
            </div>
          )}

          <button
            className="auth-submit-btn"
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
          >
            {loading
              ? <><Loader size={15} className="auth-spinner" /> Väntar…</>
              : mode === 'login' ? 'Logga in' : 'Skapa konto'}
          </button>
        </form>
      </div>
    </div>
  );
}
